import React from 'react';
import { append } from 'ramda';
import { MutationUpdaterFn } from '@apollo/client';

import {
  TEDInputUrvalType,
  IUIUrval,
} from '../../../../../../../Types/UrvalTypes';
import {
  ApolloClient,
  ApolloConsumer,
  MutationFunction as MutationFn,
  FetchResult,
} from '@apollo/client';

import { convertUIUrvalToTEDUrval } from '../../../../../Utils/convertUIUrvalToTEDUrval';
import { convertUrval } from '../../../../SavedUrval/Utils/convertSavedUrval';
import {
  RutinUrvalListQuery as IRutinUrvalListQueryData,
  RutinUrvalListQueryVariables as IRutinUrvalListQueryVariables,
} from '../../../../SavedUrval/Queries/RutinUrvalListQuery/_QueryTypes/RutinUrvalListQuery';
import rutinUrvalListQuery from '../../../../SavedUrval/Queries/RutinUrvalListQuery/RutinUrvalListQuery.gql';
import { KodplanConsumer } from '../../../../../../KodplanManager/Context/KodplanContext';
import {
  ModelContextConsumer,
  AvenyCollectionMap,
} from '../../../../../../../Model/ModelContext/ModelContext';
import mutation from './SaveNewUrvalMutation.gql';
import {
  SaveSelection as ISaveSelectionMutationData,
  SaveSelectionVariables as ISaveSelectionMutationVariables,
} from './_QueryTypes/SaveSelection';
import Logger, { graphQLError } from '../../../../../../../Utils/Logger';

export type FetchResultType = FetchResult<ISaveSelectionMutationData>;
export type SaveNewUrvalMutationFnType = MutationFn<
  ISaveSelectionMutationData,
  ISaveSelectionMutationVariables
>;

export type SaveNewUrvalMutationUpdaterType =
  MutationUpdaterFn<ISaveSelectionMutationData>;

/**
 * The component
 */
interface IOuterProps {
  dbName: string;
  collectionName: string;
  children: (
    saveUrval: (urval: IUIUrval, urvalInput: TEDInputUrvalType) => Promise<void>
  ) => React.ReactNode;
  setUrval: (newUrval: IUIUrval) => void;

  rutinID: number;
  gruppID?: string;
  usagecode?: string;
  kodplanUrval: boolean;
  showDBStandard?: boolean;
  saveNewUrvalMutationUpdater?: SaveNewUrvalMutationUpdaterType;
}

interface IInnerProps extends IOuterProps {
  apolloClient: ApolloClient<any>;
  kontoplanID: string;
  collectionMap: AvenyCollectionMap;
}

const createVariables = (
  urval: IUIUrval,
  inputUrval: TEDInputUrvalType,
  dbName: string
): ISaveSelectionMutationVariables => {
  const { savedUrvalProps: props } = urval;

  return {
    dbname: dbName,
    selection: inputUrval,
    conditions: convertUIUrvalToTEDUrval(urval),
    order:
      props != null
        ? props.order != null
          ? props.order.map(order => ({
              namn: order.namn,
              ass: order.ass,
              desc: order.desc,
            }))
          : null
        : null,
  };
};

class SaveNewUrvalMutation extends React.Component<IInnerProps> {
  saveUrval = (urval: IUIUrval, urvalInput: TEDInputUrvalType) => {
    return this.props.apolloClient
      .mutate<ISaveSelectionMutationData, ISaveSelectionMutationVariables>({
        mutation: mutation,
        variables: createVariables(urval, urvalInput, this.props.dbName),
        update: (cache, result) => {
          const {
            dbName: dbname,
            rutinID,
            collectionName: collection,
            gruppID,
            kontoplanID,
            usagecode,
          } = this.props;

          if (result.data && result.data.saveselection) {
            const data = cache.readQuery<
              IRutinUrvalListQueryData,
              IRutinUrvalListQueryVariables
            >({
              query: rutinUrvalListQuery,
              variables: {
                dbname,
                collection,
                rutinID,
                gruppID,
                kontoplanID,
                usagecode,
                kontoplanUrval: this.props.kodplanUrval,
                showDBStandard: this.props.showDBStandard,
              },
            });

            if (data && data.routineselections) {
              // Temp fix before we have separate query for anvFavorit and rutinStandard
              const saveSelection = {
                ...result.data.saveselection,
                anvFavorit: false,
                rutinStandard: false,
              };

              const newRoutineSelections = append(
                saveSelection,
                data.routineselections
              );

              cache.writeQuery({
                query: rutinUrvalListQuery,
                data: { routineselections: newRoutineSelections },
                variables: {
                  dbname,
                  rutinID,
                  collection,
                  gruppID,
                  kontoplanID,
                  kontoplanUrval: this.props.kodplanUrval,
                  usagecode,
                },
              });
            }
          }

          if (this.props.saveNewUrvalMutationUpdater) {
            this.props.saveNewUrvalMutationUpdater(cache, result);
          }
        },
      })
      .then(result => {
        if (result.errors != null) {
          Logger.error(graphQLError(result.errors));
          throw new Error(result.errors.map(e => e.message).join(', '));
        } else if (result.data == null || result.data.saveselection == null) {
          throw new Error('Got empty response');
        } else {
          // Success set selection to newly saved selection
          this.props.setUrval(
            convertUrval(
              { ...result.data.saveselection, conditionPageNames: null },
              this.props.collectionName,
              this.props.collectionMap
            )
          );
        }
      });
  };

  render() {
    return this.props.children(this.saveUrval);
  }
}

const EnhancedSaveNewUrvalMutation = (props: IOuterProps) => (
  <ApolloConsumer>
    {client => (
      <KodplanConsumer>
        {({ activeKodplan }) => (
          <ModelContextConsumer>
            {({ avenyCollectionMap }) => (
              <SaveNewUrvalMutation
                {...props}
                apolloClient={client}
                kontoplanID={activeKodplan ? activeKodplan.id : ''}
                collectionMap={avenyCollectionMap}
              />
            )}
          </ModelContextConsumer>
        )}
      </KodplanConsumer>
    )}
  </ApolloConsumer>
);

export default EnhancedSaveNewUrvalMutation;
